// src/pages/creator-panel/CallLogs/CallLogsListPage.js
// Main Call Logs entry point: two-row filter bar + paginated table.
// Click any row → opens CallDetailDialog with prev/next nav.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Alert, CircularProgress, Card, TextField, MenuItem, Select,
    Pagination, Tooltip, Checkbox, InputAdornment, Chip, Stack, IconButton,
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    Phone as PhoneIcon,
    Person as PersonIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import { useChatbots } from '../../../contexts/ChatbotContext';
import { hasPermission } from '../../../utils/permissionUtils';
import { truncateId, formatHeaderDate, formatDuration } from './utils/formatters';
import CallDetailDialog from './CallDetailDialog';

const PAGE_SIZE = 25;

const TIME_RANGE_OPTIONS = [
    { value: '1d', label: 'Last 24 hours' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'all', label: 'All time' },
];

// Call Logs shows one channel at a time. Values match CallSession.channel.
// 'text-chat' / 'recording' rows are materialized from question_improvement
// (typed vs mic voice-note) — see klonit-api materialize_text_chat_call_session.
const CHANNEL_OPTIONS = [
    { value: 'phone', label: 'Live Phone Call' },
    { value: 'live-conversation', label: 'Live Conversation' },
    { value: 'text-chat', label: 'Text Chat' },
    { value: 'recording', label: 'Recording' },
];

const computeRangeBounds = (val) => {
    if (val === 'all' || !val) return { started_after: null, started_before: null };
    const days = parseInt(val.replace('d', ''), 10);
    if (!days) return { started_after: null, started_before: null };
    const after = new Date();
    after.setDate(after.getDate() - days);
    return { started_after: after.toISOString(), started_before: null };
};

const InboundPill = ({ direction }) => {
    const isInbound = (direction || '').toLowerCase().startsWith('in');
    return (
        <Chip
            size="small"
            icon={<PhoneIcon sx={{ fontSize: 14 }} />}
            label={isInbound ? 'Inbound' : 'Outbound'}
            color={isInbound ? 'success' : 'info'}
            variant="outlined"
            sx={{ fontWeight: 600, height: 22 }}
        />
    );
};

const EndedReasonPill = ({ reason }) => {
    if (!reason) return null;
    return (
        <Chip
            size="small"
            label={reason}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500, height: 22 }}
        />
    );
};

export default function CallLogsListPage() {
    const { chatbotId: urlChatbotId } = useParams();
    const { activeChatbot } = useChatbots();
    const chatbotId = urlChatbotId || activeChatbot?.chatbot_identity;

    const [canView, setCanView] = useState(false);
    const [checkingPerm, setCheckingPerm] = useState(true);

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Filter state
    const [timeRange, setTimeRange] = useState('7d');
    const [callIdQ, setCallIdQ] = useState('');
    const [assistant, setAssistant] = useState('');
    const [assistantPhone, setAssistantPhone] = useState('');
    // 'phone' = Live Phone Call, 'live-conversation' = Live Conversation.
    // Defaults to Live Phone Call so Call Logs opens on phone data.
    const [searchParams] = useSearchParams();
    // Deep-link support: the Live Conversation / Live Phone Call settings pages
    // link here with ?channel=... so their "Logs" button lands on the right
    // channel. Unknown/missing values fall back to 'phone'.
    const channelParam = searchParams.get('channel');
    const [channel, setChannel] = useState(
        CHANNEL_OPTIONS.some(o => o.value === channelParam) ? channelParam : 'phone'
    );
    const [customerPhone, setCustomerPhone] = useState('');

    const [filterOpts, setFilterOpts] = useState({
        assistants: [], assistant_phone_numbers: [], customer_phone_numbers: [], directions: [],
    });

    // Selection (checkbox state)
    const [selected, setSelected] = useState(new Set());

    // Detail dialog state
    const [openSessionId, setOpenSessionId] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            const v = await hasPermission('call-logs', 'view');
            if (!active) return;
            setCanView(v);
            setCheckingPerm(false);
        })();
        return () => { active = false; };
    }, []);

    // Fetch dropdown options once we know the chatbot
    useEffect(() => {
        if (!chatbotId || !canView) return;
        axiosInstance.get(`/api/chatbots/${chatbotId}/call-sessions/filters`)
            .then(res => setFilterOpts(res?.data?.data || {}))
            .catch(() => { /* non-fatal — filters fall back to free-text only */ });
    }, [chatbotId, canView]);

    const fetchRows = useCallback(async (pageNum = 1) => {
        if (!chatbotId) return;
        setLoading(true);
        setError('');
        try {
            const range = computeRangeBounds(timeRange);
            const params = {
                page: pageNum, limit: PAGE_SIZE,
                channel,
                ...(callIdQ ? { call_id: callIdQ } : {}),
                ...(assistant ? { assistant } : {}),
                ...(customerPhone && channel === 'phone' ? { customer_phone: customerPhone } : {}),
                ...(range.started_after ? { started_after: range.started_after } : {}),
                ...(range.started_before ? { started_before: range.started_before } : {}),
            };
            const res = await axiosInstance.get(`/api/chatbots/${chatbotId}/call-sessions`, { params });
            setRows(res?.data?.data || []);
            setTotal(res?.data?.total || 0);
            setPage(pageNum);
        } catch (e) {
            setError('Could not load call logs.');
        } finally {
            setLoading(false);
        }
    }, [chatbotId, channel, callIdQ, assistant, customerPhone, timeRange]);

    // Debounced fetch on filter change
    useEffect(() => {
        if (checkingPerm || !canView) return;
        const t = setTimeout(() => fetchRows(1), 300);
        return () => clearTimeout(t);
    }, [fetchRows, checkingPerm, canView]);

    const toggleSelect = (id, evt) => {
        evt.stopPropagation();
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selected.size === rows.length) setSelected(new Set());
        else setSelected(new Set(rows.map(r => r.id)));
    };

    const handleNavigate = (newId) => setOpenSessionId(newId);

    const filteredCallsForNav = useMemo(() => rows, [rows]);

    // Cross-page nav: when the dialog hits the first/last row of the current
    // page, fetch the adjacent page and open its boundary row. Direction is
    // 'prev' (jump to LAST row of the previous page) or 'next' (FIRST row of
    // the next page). Returns true if a fetch was kicked off, false if we're
    // already at the dataset boundary.
    const handleEdgeNavigate = useCallback(async (direction) => {
        const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
        if (direction === 'next' && page >= totalPages) return false;
        if (direction === 'prev' && page <= 1) return false;
        const targetPage = direction === 'next' ? page + 1 : page - 1;
        try {
            const range = computeRangeBounds(timeRange);
            const params = {
                page: targetPage, limit: PAGE_SIZE,
                channel,
                ...(callIdQ ? { call_id: callIdQ } : {}),
                ...(assistant ? { assistant } : {}),
                ...(customerPhone && channel === 'phone' ? { customer_phone: customerPhone } : {}),
                ...(range.started_after ? { started_after: range.started_after } : {}),
                ...(range.started_before ? { started_before: range.started_before } : {}),
            };
            setLoading(true);
            const res = await axiosInstance.get(`/api/chatbots/${chatbotId}/call-sessions`, { params });
            const newRows = res?.data?.data || [];
            setRows(newRows);
            setTotal(res?.data?.total || 0);
            setPage(targetPage);
            if (newRows.length) {
                const target = direction === 'next' ? newRows[0] : newRows[newRows.length - 1];
                setOpenSessionId(target.id);
            }
            return true;
        } catch (e) {
            setError('Could not load adjacent page.');
            return false;
        } finally {
            setLoading(false);
        }
    }, [chatbotId, channel, page, total, timeRange, callIdQ, assistant, customerPhone]);

    if (checkingPerm) {
        return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    }
    if (!canView) {
        return <Alert severity="warning" sx={{ m: 3 }}>You don't have permission to view call logs.</Alert>;
    }

    // Only Live Phone Call has telephony numbers. Every other channel
    // (Live Conversation, Text Chat, Recording) swaps the two phone columns
    // for Started / Duration, which are populated for every channel.
    const isNonPhone = channel !== 'phone';

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">Call Logs</Typography>
                {/* Reloads the current page (preserves pagination + active filters). */}
                <Tooltip title="Refresh">
                    <span>
                        <IconButton size="small" onClick={() => fetchRows(page)} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* Filter bar — single row.
                Squads, Saved-filters, Column-settings, and Call-Types fields were
                placeholders without backing data and have been removed until the
                features actually exist (avoids the "looks broken / disabled" feel). */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 1.5, alignItems: 'end', mb: 2 }}>
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Channel</Typography>
                    <Select
                        size="small" fullWidth value={channel}
                        onChange={(e) => setChannel(e.target.value)}
                        startAdornment={<InputAdornment position="start"><PhoneIcon sx={{ fontSize: 16 }} /></InputAdornment>}
                    >
                        {CHANNEL_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        Time Range {timeRange !== 'all' && (
                            <Tooltip title="Clear">
                                <CloseIcon
                                    sx={{ fontSize: 12, cursor: 'pointer', verticalAlign: 'middle' }}
                                    onClick={() => setTimeRange('all')}
                                />
                            </Tooltip>
                        )}
                    </Typography>
                    <Select
                        size="small" fullWidth value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        startAdornment={<InputAdornment position="start"><CalendarIcon sx={{ fontSize: 16 }} /></InputAdornment>}
                    >
                        {TIME_RANGE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Call ID</Typography>
                    <TextField size="small" fullWidth placeholder="Call ID" value={callIdQ}
                        onChange={(e) => setCallIdQ(e.target.value)} />
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Assistants</Typography>
                    <Select size="small" fullWidth displayEmpty value={assistant}
                        onChange={(e) => setAssistant(e.target.value)}>
                        <MenuItem value=""><em>Assistants</em></MenuItem>
                        {(filterOpts.assistants || []).map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                    </Select>
                </Box>
                {/* Phone-number filters apply only to Live Phone Call — hidden for
                    Live Conversation / Text Chat / Recording, which have no numbers. */}
                {!isNonPhone && (
                    <>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Assistant Phone</Typography>
                            <Select size="small" fullWidth displayEmpty value={assistantPhone}
                                onChange={(e) => setAssistantPhone(e.target.value)}>
                                <MenuItem value=""><em>All numbers</em></MenuItem>
                                {(filterOpts.assistant_phone_numbers || []).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Customer Phone</Typography>
                            <TextField
                                size="small" fullWidth placeholder="Search customer number"
                                value={customerPhone}
                                onChange={(e) => setCustomerPhone(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
                            />
                        </Box>
                    </>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card sx={{ overflow: 'hidden' }}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '40px 100px 220px 180px 180px 110px 130px',
                    gap: 1.5,
                    px: 2, py: 1.5,
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase',
                    borderBottom: '1px solid', borderColor: 'divider',
                }}>
                    <Box>
                        <Checkbox
                            size="small"
                            checked={rows.length > 0 && selected.size === rows.length}
                            indeterminate={selected.size > 0 && selected.size < rows.length}
                            onChange={toggleSelectAll}
                            sx={{ p: 0 }}
                        />
                    </Box>
                    <Box>Call ID</Box>
                    <Box>Assistant / Squad</Box>
                    {isNonPhone ? (
                        <>
                            <Box>Started</Box>
                            <Box>Duration</Box>
                        </>
                    ) : (
                        <>
                            <Box>Assistant Phone Number</Box>
                            <Box>Customer Phone Number</Box>
                        </>
                    )}
                    <Box>Type</Box>
                    <Box>Ended Reason</Box>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!loading && rows.length === 0 && (
                    <Typography color="text.disabled" sx={{ textAlign: 'center', py: 6, fontStyle: 'italic' }}>
                        No call logs yet for this chatbot.
                    </Typography>
                )}

                {!loading && rows.map(r => (
                    <Box
                        key={r.id}
                        onClick={() => setOpenSessionId(r.id)}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '40px 100px 220px 180px 180px 110px 130px',
                            gap: 1.5,
                            alignItems: 'center',
                            px: 2, py: 1.5,
                            borderBottom: '1px solid', borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                        }}
                    >
                        <Box onClick={(e) => toggleSelect(r.id, e)}>
                            <Checkbox size="small" checked={selected.has(r.id)} sx={{ p: 0 }} />
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                            {truncateId(r.session_uid)}
                        </Typography>
                        <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                    {r.assistant_name || '—'}
                                </Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', display: 'block' }} noWrap>
                                {truncateId(r.session_uid)}
                            </Typography>
                        </Box>
                        {isNonPhone ? (
                            <>
                                <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
                                    {formatHeaderDate(r.started_at) || '—'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
                                    {formatDuration(r.duration_seconds)}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.primary' }} noWrap>
                                        {r.assistant_phone_number ? truncateId(r.assistant_phone_number, 8, 0) : '—'}
                                    </Typography>
                                    {r.assistant_phone_label && (
                                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }} noWrap>
                                            {r.assistant_phone_label}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
                                    {r.customer_phone_formatted || r.customer_phone_raw || '—'}
                                </Typography>
                            </>
                        )}
                        <Box><InboundPill direction={r.direction} /></Box>
                        <Box><EndedReasonPill reason={r.ended_reason} /></Box>
                    </Box>
                ))}

                {total > PAGE_SIZE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <Pagination
                            count={Math.ceil(total / PAGE_SIZE)}
                            page={page}
                            onChange={(_, p) => fetchRows(p)}
                            color="primary"
                            size="small"
                            sx={{
                                // The global `.Mui-selected { color:#007bff !important }` rule in
                                // assets/css/custom.css (authored for Tabs) leaks onto the selected
                                // pagination item, rendering its number blue-on-blue (invisible).
                                // Re-assert the intended white-on-primary contrast for the active page.
                                '& .MuiPaginationItem-root.Mui-selected': {
                                    color: '#fff !important',
                                    bgcolor: 'primary.main',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                },
                            }}
                        />
                    </Box>
                )}
            </Card>

            <CallDetailDialog
                open={!!openSessionId}
                onClose={() => setOpenSessionId(null)}
                sessionId={openSessionId}
                filteredCalls={filteredCallsForNav}
                onNavigateToSession={handleNavigate}
                onEdgeNavigate={handleEdgeNavigate}
                pageInfo={{ page, totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)) }}
            />
        </Box>
    );
}
